/**
 * "They wrote back" — the Candidate Requests queue, indexed for the Scheduling tab.
 *
 * A recruiter proposes case times by email, the candidate replies, and until now the two halves
 * lived in different tabs: the reply was read in 📥 Requests and the interview was booked in
 * 📅 Scheduling, with nothing on the scheduling card saying an answer had arrived (Theresa, Sep
 * 2026). This is the index behind the card's chip.
 *
 * ── Which slots it claims they confirmed ─────────────────────────────────────────────────────
 *
 * Deliberately CONSERVATIVE, and it claims nothing it cannot show. A reply is matched to one of
 * the times WE offered only when it names both the DATE and the START HOUR of that slot, or the
 * numbered marker our own proposal email puts in front of it (1️⃣, 2️⃣ …). Anything looser —
 * matching a bare hour, or a lone day number — turns "see you Tuesday" into a claim about a
 * specific 90-minute slot, and a recruiter who books on that is booking on a guess.
 *
 * So the chip has two honest states: it names the slots it is sure of, or it says only that a
 * reply arrived and links to it. It never paraphrases the candidate.
 */
import type { InboundCategory, InboundRequest } from '../../types/taInboundRequests';

/** Berlin, like every other time this board renders. */
const TZ = 'Europe/Berlin';

/**
 * Categories that mean "this is about when the interview happens". A reply about anything else
 * (a document, a question about the role) is not a scheduling answer and must not raise a chip
 * that says one arrived.
 */
const SCHEDULING_REPLIES = new Set<InboundCategory>([
  'scheduling_confirm',
  'availability_window',
  'scheduling_request',
  'reschedule',
]);

export interface CandidateReply {
  threadId: string;
  gmailUrl: string;
  /** ISO — the most recent scheduling reply from this candidate. */
  receivedAt: string;
  category: InboundCategory;
  /** ISO starts, drawn from the slots WE offered — never a time parsed out of their prose. */
  confirmedSlots: string[];
  /** Their own words, for the card's tooltip. Never reworded. */
  question: string;
}

const partsOf = (iso: string) => {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms);
  const get = (opt: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat('en-GB', { timeZone: TZ, ...opt }).format(d);
  return {
    day: Number(get({ day: 'numeric' })),
    month: Number(get({ month: 'numeric' })),
    hour: Number(get({ hour: 'numeric', hour12: false })),
  };
};

/** `1️⃣` … `9️⃣` — the markers our own proposal email numbers the options with. */
const KEYCAP = ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣'];

/**
 * Does this reply name this slot?
 *
 * @param text the candidate's message
 * @param start ISO start of a slot we offered
 * @param index 0-based position in the offer, for the keycap marker
 */
export function replyNamesSlot(text: string, start: string, index: number): boolean {
  const body = `${text || ''}`;
  if (!body.trim()) return false;
  // The marker we wrote ourselves is the one unambiguous signal in the whole message.
  if (index < KEYCAP.length && body.includes(KEYCAP[index])) return true;

  const p = partsOf(start);
  if (!p) return false;
  // The DATE, in the forms people actually write: 22.7., 22/7, 22 July, July 22 — all of which
  // contain the day next to the month. A day number on its own is not enough: "22" appears in
  // phone numbers, addresses and years.
  // `0?` on both halves: "22.07." is the ordinary German way to write it, and \b7\b does not
  // match inside "07" — the zero and the seven are both word characters, so there is no boundary.
  const dateNamed = new RegExp(
    `\\b0?${p.day}\\s*[./-]\\s*0?${p.month}\\b|\\b0?${p.month}\\s*[./-]\\s*0?${p.day}\\b`
  ).test(body);
  if (!dateNamed) return false;

  // AND the start hour, written the ways people actually write it. Every alternative is
  // ANCHORED — to a separator (10:00), a unit (10 Uhr), the RIGHT meridiem, or a preposition
  // (um 10). There is deliberately no bare-number alternative: with the date already matched
  // somewhere in the message, a loose `\b10\b` turned "22.7. passt, ruf mich an unter
  // 0176 22 10 00" into a confirmed 10:00 slot (CodeRabbit, PR #354) — a phone number read as a
  // booking instruction, which is the single failure this matcher exists to prevent.
  const hour = p.hour;
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  // The candidate's own clock half, not either: "22.7 at 10pm" is not a 10:00 slot.
  const meridiem = hour < 12 ? 'am' : 'pm';
  // Every interpolation is a NUMBER out of Intl (see partsOf), so nothing candidate-written
  // reaches this pattern — the usual ReDoS concern with a built regex does not apply here.
  return new RegExp(
    [
      `\\b0?${hour}\\s*[:.]\\s*\\d{2}\\b`,
      `\\b0?${hour}\\s*(uhr|h\\b)`,
      `\\b0?${hour12}\\s*${meridiem}\\b`,
      `\\b(um|ab|at|from)\\s*0?${hour}\\b`,
    ].join('|'),
    'i'
  ).test(body);
}

/**
 * Index the loaded Requests queue by Lever opportunity.
 *
 * Keeps the MOST RECENT scheduling reply per candidate: an older "can we move it?" under a newer
 * "Tuesday works" would be the wrong thing to show, and showing both would make the card a mail
 * client.
 *
 * @param offeredByCandidate opportunityId → the ISO starts we proposed, in the order we proposed
 *        them (the keycap markers count from there).
 */
export function indexRepliesByCandidate(
  items: InboundRequest[],
  offeredByCandidate: Map<string, string[]> = new Map()
): Map<string, CandidateReply> {
  const byCandidate = new Map<string, CandidateReply>();
  for (const item of items || []) {
    if (!item?.opportunityId || !SCHEDULING_REPLIES.has(item.category)) continue;
    const existing = byCandidate.get(item.opportunityId);
    if (existing && Date.parse(existing.receivedAt) >= Date.parse(item.receivedAt)) continue;

    const offered = offeredByCandidate.get(item.opportunityId) || [];
    const text = item.questionFull || item.question || '';
    byCandidate.set(item.opportunityId, {
      threadId: item.threadId,
      gmailUrl: item.gmailUrl,
      receivedAt: item.receivedAt,
      category: item.category,
      // Only for a reply that reads as an ANSWER. Someone asking to move a booked interview is
      // not confirming one of the times we proposed.
      confirmedSlots:
        item.category === 'scheduling_confirm' || item.category === 'availability_window'
          ? offered.filter((start, i) => replyNamesSlot(text, start, i))
          : [],
      question: text,
    });
  }
  return byCandidate;
}
